/**
 * Which customer is this browser tab looking at? (Plan E, E4)
 *
 * Plan E removes per-customer subdomains. Every screen lives under
 * `/{slug}/…` and there is ONE login page at `/login` for the whole
 * fleet. So the first path segment is the customer — except when it
 * isn't, and telling those apart without guessing is what this module
 * is for.
 *
 * THE RULE, and why it is not a table of route names.
 *
 * The obvious approach is "the first segment is the slug unless it is
 * one of our routes" and then listing `projects`, `settings`, `admin`,
 * `files`… That list is a liability: it has to stay in step with two
 * routers, and it silently mis-classifies a customer who happens to be
 * named after one of them.
 *
 * Instead the question is answered with something we actually know:
 * sessions are stored PER CUSTOMER (`builder.token:acme`), so a segment we
 * hold a token for is a customer, full stop. No list, and a customer
 * literally named `projects` resolves correctly for its own users while
 * an old unprefixed `/projects/gating` link still resolves correctly
 * for everyone else.
 *
 * `login` never collides because it is reserved fleet-wide in
 * `tenancy.registry.RESERVED_SLUGS` — nobody can be named that.
 *
 * WHY STORAGE IS KEYED AT ALL. Before Plan E each customer had its own
 * subdomain, hence its own origin, hence its own localStorage. One
 * fleet address collapses that: a single `builder.token` would mean a
 * person with accounts at two customers can only hold one session,
 * signing into the second silently ends the first, and — worse — the
 * token for A rides along on requests for B, which the server answers
 * with a `tid` mismatch 401. The user experiences that as "it logs me
 * out by itself". Keying by slug is what prevents it.
 */

const TOKEN_PREFIX = "builder.token";

/** Mirrors `tenancy.registry.SLUG_RE`. */
const SLUG_RE = /^[a-z][a-z0-9-]{0,62}$/;

function firstSegment(pathname: string): string {
  return pathname.split("/")[1] ?? "";
}

/** The storage key holding the session for one customer. */
export function tokenKeyFor(slug: string): string {
  return `${TOKEN_PREFIX}:${slug}`;
}

function hasSessionFor(slug: string): boolean {
  try {
    return window.localStorage.getItem(tokenKeyFor(slug)) !== null;
  } catch {
    // Private mode can throw. Treat as "no session" — the worst case is
    // a trip through the login page, never a wrong customer.
    return false;
  }
}

/** Every customer this browser currently holds a session for. */
export function storedSlugs(): string[] {
  const found: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(`${TOKEN_PREFIX}:`)) {
        found.push(key.slice(TOKEN_PREFIX.length + 1));
      }
    }
  } catch {
    /* private mode — fall through to an empty list */
  }
  return found;
}

/**
 * The customer this URL addresses, or `null`.
 *
 * `null` genuinely means "unknown", never "the usual one". A caller
 * that gets it must send the user to the login page or ask them, not
 * pick a customer — picking is how somebody ends up looking at another
 * company's numbers.
 */
export function slugFromPath(
  pathname: string = typeof window === "undefined"
    ? "/"
    : window.location.pathname,
): string | null {
  const seg = firstSegment(pathname);
  if (!seg || !SLUG_RE.test(seg)) return null;
  return hasSessionFor(seg) ? seg : null;
}

/**
 * `""` or `"/acme"` — what to put in front of an API path.
 *
 * Read fresh on every call rather than captured at module load: the
 * login page navigates into `/{slug}/` and a value frozen at import
 * would still say "no customer" for every request afterwards.
 */
export function tenantPrefix(): string {
  const slug = slugFromPath();
  return slug ? `/${slug}` : "";
}

/**
 * Where an unprefixed URL should go, or `null` to leave it alone.
 *
 * Handles the deep link that predates Plan E — a bookmark or an e-mail
 * link to `/projects/gating`. With exactly one session stored the
 * answer is unambiguous, so the user is moved rather than bounced
 * through a login they do not need. With several, it is genuinely
 * ambiguous and this returns `null`: the caller sends them to the
 * login page, where naming their address settles it. Guessing between
 * two customers would be the one unrecoverable mistake here.
 */
export function redirectForUnprefixedPath(
  pathname: string = typeof window === "undefined"
    ? "/"
    : window.location.pathname,
): string | null {
  if (slugFromPath(pathname) !== null) return null;
  const slugs = storedSlugs();
  if (slugs.length !== 1) return null;
  return `/${slugs[0]}${pathname}`;
}
